/**
 * Tenant onboarding checklist — pure state-derivation logic (spec §2/§3).
 *
 * Extracted from AdminOnboarding.tsx so the dependency-locking, progress
 * counting, and tier-entitlement rules are unit-tested without a DOM. No React
 * here. Mirrors the `get_onboarding_state` RPC row shape.
 */

export type OnboardingStatus =
  | 'pending'
  | 'in_progress'
  | 'waiting_external'
  | 'complete'
  | 'skipped';
export type OnboardingKind = 'copilot' | 'external' | 'manual';

export type OnboardingStateRow = {
  step_id: string;
  title: string;
  description: string | null;
  kind: OnboardingKind;
  sort_order: number;
  depends_on: string[];
  min_tier_rank: number | null;
  entitled: boolean;
  status: OnboardingStatus;
  completed_at: string | null;
  updated_by: string | null;
  detail: Record<string, unknown>;
};

/** A step counts as "done" for dependency + progress purposes when it is
 * complete OR explicitly skipped (a skipped step no longer blocks its dependents). */
export function isStepDone(status: OnboardingStatus): boolean {
  return status === 'complete' || status === 'skipped';
}

/** step_id → done? for O(1) dependency resolution. */
export function buildDoneMap(rows: readonly OnboardingStateRow[]): Map<string, boolean> {
  const m = new Map<string, boolean>();
  for (const r of rows) m.set(r.step_id, isStepDone(r.status));
  return m;
}

/** step_id → title, for rendering "complete X first" hints. */
export function buildTitleMap(rows: readonly OnboardingStateRow[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const r of rows) m.set(r.step_id, r.title);
  return m;
}

/** The dependency step_ids of `row` that are not yet done. */
export function unmetDependencies(row: OnboardingStateRow, done: Map<string, boolean>): string[] {
  return row.depends_on.filter((d) => done.get(d) !== true);
}

/** A step is "locked" only when it hasn't started AND some dependency is unmet.
 * (A step already in progress / waiting / done is never shown as locked.) */
export function isStepLocked(row: OnboardingStateRow, done: Map<string, boolean>): boolean {
  return row.status === 'pending' && unmetDependencies(row, done).length > 0;
}

/** Progress counts ENTITLED steps only — tier-locked steps aren't required for
 * this tenant to reach go-live, so they don't inflate the denominator. */
export function computeProgress(rows: readonly OnboardingStateRow[]): {
  completed: number;
  required: number;
} {
  const entitled = rows.filter((r) => r.entitled);
  return {
    completed: entitled.filter((r) => isStepDone(r.status)).length,
    required: entitled.length,
  };
}
